/**
 * Unit maths shared by every mastersheet view. The portfolio ("All Properties")
 * table and a single property's sheet both feed the summary tiles from rows of one
 * shape: { unit_no, tenant, basic_rent, target_rent, lease_end, sd, sqft }.
 *
 * Inactive properties never reach these helpers: the server scopes both the
 * portfolio and the property dropdown to `status: "Active"`.
 */

export const money = (n) =>
  n === null || n === undefined
    ? "—"
    : new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(Number(n) || 0);

/** Compact money for the summary tiles — $1.2M / $48.3k / $920. */
export const moneyShort = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
  return `$${Math.round(v)}`;
};

export const DAY_MS = 86400000;

/**
 * Whole days from today to `v`; null when there is no usable date. `asOf`
 * (YYYY-MM-DD) measures from that day instead — the Historic sheet's chosen date.
 */
export const daysUntil = (v, asOf) => {
  if (!v) return null;
  const t = new Date(String(v).slice(0, 10)).getTime();
  if (!Number.isFinite(t)) return null;
  const today = asOf ? new Date(String(asOf).slice(0, 10)) : new Date();
  if (!Number.isFinite(today.getTime())) return null;
  if (!asOf) today.setHours(0, 0, 0, 0);
  return Math.round((t - today.getTime()) / DAY_MS);
};

/**
 * The same three variance bands the per-property sheet paints: at/above target,
 * short by under $75, short by $75 or more.
 */
export const varianceOf = (row) => {
  const basic = Number(row.basic_rent);
  const target = Number(row.target_rent);
  if (!Number.isFinite(basic) || !Number.isFinite(target) || (!basic && !target)) {
    return { diff: null, band: null };
  }
  const diff = target - basic;
  if (diff <= 0) return { diff, band: "up", cls: "pft-var--up", indicator: "▲", label: `+${money(Math.abs(diff))}` };
  if (diff < 75) return { diff, band: "near", cls: "pft-var--near", indicator: "~", label: `-${money(diff)}` };
  return { diff, band: "down", cls: "pft-var--down", indicator: "▼", label: `-${money(diff)}` };
};

/** A tenant cell that means nobody: blank, a placeholder token, or the word itself. */
const VACANT_TOKENS = new Set(["", "n/a", "na", "-", "--", "—", "vacant", "empty", "none", "null", "undefined"]);

export const isOccupied = (r) => !VACANT_TOKENS.has(String(r?.tenant || "").trim().toLowerCase());

/** Numeric-aware unit order: 7, 101, 102, 501, 1501 — never 1501 ahead of 501. */
export const compareUnitNo = (a, b) =>
  String(a ?? "").trim().localeCompare(String(b ?? "").trim(), undefined, { numeric: true, sensitivity: "base" });

/** "50" matches 501 and 502 but not 1501: a plain prefix on the unit number only. */
export const unitMatchesPrefix = (unitNo, query) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  return String(unitNo ?? "").trim().toLowerCase().startsWith(q);
};

/**
 * Tenant text from a sheet item: a string, or an array of names / person objects.
 * "N/A" placeholders count as nobody.
 */
export const tenantText = (value) => {
  if (!value) return "";
  const names = (Array.isArray(value) ? value : [value])
    .map((t) => {
      if (typeof t === "string") return t.trim();
      if (t && typeof t === "object") return String(t.name || `${t.firstName || ""} ${t.lastName || ""}`).trim();
      return "";
    })
    .filter((n) => n && !VACANT_TOKENS.has(n.toLowerCase()));
  return names.join(", ");
};

/** A single property's sheet items (server field names) → the row shape the tiles read. */
export const unitRowsFromItems = (items) =>
  (items || []).map((item) => ({
    unit_no: item.unitNo,
    tenant: tenantText(item.tenants || item.tenant),
    basic_rent: item.basicRent,
    target_rent: item.targetRent,
    lease_end: item.leaseEndDmy,
  }));

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/**
 * The Vacant / Already Rented window (owner, 2026-09-28): vacant today, or the lease
 * ends between today and the last day of NEXT month — on Sep 28 that runs to Oct 31.
 * `asOf` (YYYY-MM-DD) is the Historic sheet's chosen day. Mirrors the server's
 * Server/src/services/mastersheet/next-month-window.ts; keep the two in step.
 */
export const nextMonthWindow = (asOf) => {
  let y;
  let m;
  if (asOf) [y, m] = String(asOf).slice(0, 10).split("-").map(Number);
  else {
    const now = new Date();
    y = now.getFullYear();
    m = now.getMonth() + 1;
  }
  const last = new Date(Date.UTC(y, m + 1, 0));
  const month = MONTHS[last.getUTCMonth()];
  const end = last.toISOString().slice(0, 10);
  return { end, monthLabel: `${month} ${last.getUTCFullYear()}`, endLabel: `${month.slice(0, 3)} ${last.getUTCDate()}`, daysToEnd: daysUntil(end, asOf) };
};

/**
 * Where a row falls: "VACANT" (nobody today), "ENDING" (lease ends inside the window),
 * "PAST_END" (lease already ended, tenant still listed — shown apart, never counted),
 * or null.
 */
export const availabilityOf = (r, win, asOf) => {
  if (!isOccupied(r)) return "VACANT";
  const d = daysUntil(r.lease_end, asOf);
  if (d === null) return null;
  if (d < 0) return "PAST_END";
  return d <= win.daysToEnd ? "ENDING" : null;
};

/**
 * Everything the tiles and the totals row show, over exactly the rows given.
 *
 * Target rent and square feet describe the building, so every unit counts.
 * Basic rent, the deposit, the variance bands and "leases ending" describe the
 * people in it: a vacant unit contributes nothing, whatever stale figures its
 * row still carries from the last tenancy.
 */
export const summarizeUnits = (rows, asOf) => {
  const t = { units: rows.length, occupied: 0, basic: 0, target: 0, sd: 0, sqft: 0, up: 0, near: 0, down: 0, expiring: 0, rentedVacant: 0, rentedExpiring: 0, pastEnd: 0, pastEndRented: 0 };
  t.window = nextMonthWindow(asOf);
  // "Already rented" needs the portfolio's `signed_lease` flag; rows without it (a single
  // sheet's items) leave `hasRentedInfo` false and the tile is not shown.
  t.hasRentedInfo = rows.some((r) => typeof r.signed_lease === "boolean");
  for (const r of rows) {
    const occupied = isOccupied(r);
    t.target += Number(r.target_rent) || 0;
    t.sqft += Number(r.sqft) || 0;
    if (!occupied) {
      if (r.signed_lease === true) t.rentedVacant += 1;
      continue;
    }
    t.occupied += 1;
    t.basic += Number(r.basic_rent) || 0;
    t.sd += Number(r.sd) || 0;
    const { band } = varianceOf(r);
    if (band === "up") t.up += 1;
    else if (band === "near") t.near += 1;
    else if (band === "down") t.down += 1;
    const where = availabilityOf(r, t.window, asOf);
    if (where === "ENDING") {
      t.expiring += 1;
      if (r.signed_lease === true) t.rentedExpiring += 1;
    } else if (where === "PAST_END") {
      t.pastEnd += 1;
      if (r.signed_lease === true) t.pastEndRented += 1;
    }
  }
  t.rented = t.rentedVacant + t.rentedExpiring;
  t.vacant = t.units - t.occupied;
  t.goingVacant = t.vacant + t.expiring;
  t.gap = t.target - t.basic;
  t.occupancy = t.units ? Math.round((t.occupied / t.units) * 100) : 0;
  return t;
};

const byPlace = (a, b) =>
  String(a.property_name || "").localeCompare(String(b.property_name || "")) ||
  String(a.building_name || "").localeCompare(String(b.building_name || "")) ||
  compareUnitNo(a.unit_no, b.unit_no);

/**
 * The tiles' popover: every unit behind the Vacant and Already Rented numbers, plus a
 * per-building summary. `units` are the counted ones (vacant now + ending in the
 * window); `pastEnd` are the not-counted past-end leases.
 */
export const availabilityDetail = (rows, asOf) => {
  const window = nextMonthWindow(asOf);
  const units = [];
  const pastEnd = [];
  const groups = new Map();
  for (const r of rows) {
    const where = availabilityOf(r, window, asOf);
    if (!where) continue;
    const entry = {
      key: r.item_id || `${r.property_id}|${r.building_name}|${r.unit_no}`,
      where,
      property_name: r.property_name || "",
      building_name: r.building_name || "",
      unit_no: r.unit_no,
      tenant: r.tenant || "",
      lease_end: r.lease_end || null,
      target_rent: Number(r.target_rent) || null,
      rented: r.signed_lease === true,
      incoming: r.incoming_lease || null,
    };
    if (where === "PAST_END") {
      pastEnd.push(entry);
      continue;
    }
    units.push(entry);
    const gk = `${entry.property_name}|${entry.building_name}`;
    let g = groups.get(gk);
    if (!g) {
      g = { key: gk, property_name: entry.property_name, building_name: entry.building_name, vacant: 0, ending: 0, rented: 0 };
      groups.set(gk, g);
    }
    if (where === "VACANT") g.vacant += 1;
    else g.ending += 1;
    if (entry.rented) g.rented += 1;
  }
  units.sort(byPlace);
  pastEnd.sort(byPlace);
  return { window, units, pastEnd, groups: [...groups.values()].sort(byPlace) };
};
